import type { LeagueSummary } from "@softmax/observatory-client/types";

function selectSettingsFields({
  source,
  fields,
}: {
  source: object;
  fields: readonly string[];
}): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(source).filter(
      ([key, value]) => fields.includes(key) && value !== undefined,
    ),
  );
}

function slimSettings(settings: NonNullable<LeagueSummary["settings"]> | null) {
  if (!settings || typeof settings !== "object") return settings;

  const result = selectSettingsFields({
    source: settings,
    fields: ["round_interval_minutes"],
  });
  const { campaign, ladder } = settings;

  if (campaign && typeof campaign === "object") {
    result.campaign = selectSettingsFields({
      source: campaign,
      fields: ["enabled"],
    });
  }

  if (ladder && typeof ladder === "object") {
    const selectedLadder = selectSettingsFields({
      source: ladder,
      fields: ["enabled"],
    });

    if (
      "ranking" in ladder &&
      ladder.ranking &&
      typeof ladder.ranking === "object"
    ) {
      selectedLadder.ranking = selectSettingsFields({
        source: ladder.ranking,
        fields: ["algorithm", "margin_scale"],
      });
    }

    result.ladder = selectedLadder;
  }

  return result;
}

// The browser needs identity and scoring settings, not the full league prose and policy lists.
export function slimLeagueForTheater(league: LeagueSummary): LeagueSummary {
  const {
    description: _description,
    rules: _rules,
    notes: _notes,
    filler_policy_version_ids: _fillerPolicyVersionIds,
    seed_policy_version_ids: _seedPolicyVersionIds,
    participation_url: _participationUrl,
    wiki_markdown_url: _wikiMarkdownUrl,
    forum_markdown_url: _forumMarkdownUrl,
    logs_reporter_id: _logsReporterId,
    settings,
    commissioner_config: commissionerConfig,
    ...slim
  } = league;
  const result: LeagueSummary = slim;

  if (settings !== undefined) result.settings = slimSettings(settings);

  if (commissionerConfig !== undefined) {
    result.commissioner_config =
      commissionerConfig === null
        ? null
        : selectSettingsFields({
            source: commissionerConfig,
            fields: ["schedule_interval_minutes"],
          });
  }

  return result;
}
